/** @jsxImportSource @emotion/react */
import { css } from '@emotion/react'

import React from 'react'
import { View, Text, Divider, Image, Button } from '@components/atoms'
import { ShopOutline } from '@components/molecules'
import { Footer } from '@components/systems'

import gal1 from '@public/images/pages/shop/kedem4/gallery/kedem4-gallery1.jpg'
import gal2 from '@public/images/pages/shop/kedem4/gallery/kedem4-gallery2.jpg'
import gal3 from '@public/images/pages/shop/kedem4/gallery/kedem4-gallery3.jpg'
import gal4 from '@public/images/pages/shop/kedem4/gallery/kedem4-gallery4.jpg'
import gal5 from '@public/images/pages/shop/kedem4/gallery/kedem4-gallery5.jpg'
import gal6 from '@public/images/pages/shop/kedem4/gallery/kedem4-gallery6.jpg'

import thumb1 from '@public/images/pages/shop/kedem4/thumbnail/kedem4-thumb1.jpg'
import thumb2 from '@public/images/pages/shop/kedem4/thumbnail/kedem4-thumb2.jpg'
import thumb3 from '@public/images/pages/shop/kedem4/thumbnail/kedem4-thumb3.jpg'
import thumb4 from '@public/images/pages/shop/kedem4/thumbnail/kedem4-thumb4.jpg'
import thumb5 from '@public/images/pages/shop/kedem4/thumbnail/kedem4-thumb5.jpg'
import thumb6 from '@public/images/pages/shop/kedem4/thumbnail/kedem4-thumb6.jpg'

import content1 from '@public/images/pages/shop/kedem4/contents/kedem4-1.jpg'
import content2 from '@public/images/pages/shop/kedem4/contents/kedem4-2.jpg'
import content3 from '@public/images/pages/shop/kedem4/contents/kedem4-3.jpg'
import dinamicCc from '@public/images/pages/shop/kedem4/contents/kedem4-dinamic-cc.jpg'
import contentEnd from '@public/images/pages/shop/kedem4/contents/kedem4-end.jpg'

import config from '@configs'

import { useAppSelector } from '@redux/index'
import { SeasonProduct, SeasonProductType } from '@schemas'
import { grey } from '@mui/material/colors'

const ShopKedem4Page = () => {
	const now = new Date()

	const kdm4Manual = config.manual('KDM4')

	const seasonProductList: SeasonProductType[] = useAppSelector(
		state => state.shop.productList,
	)
	const thisProduct: SeasonProductType = React.useMemo(() => {
		const filterRes: SeasonProductType[] = seasonProductList.filter(
			aProduct => aProduct.productCode === 'KDM4',
		)

		if (filterRes.length === 0) {
			return new SeasonProduct()
		}

		return filterRes[0]
	}, [seasonProductList])

	const images = [
		{
			original: gal1,
			thumbnail: thumb1,
		},
		{
			original: gal2,
			thumbnail: thumb2,
		},
		{
			original: gal3,
			thumbnail: thumb3,
		},
		{
			original: gal4,
			thumbnail: thumb4,
		},
		{
			original: gal5,
			thumbnail: thumb5,
		},
		{
			original: gal6,
			thumbnail: thumb6,
		},
	]

	const [qty, setQty] = React.useState<number>(1)

	const onManualOpenClick = (code: string, language: string) => {
		const pdfUrlArr: { [key: string]: { title: string; link: string } } =
			config.manual(code)
		const pdfUrl = pdfUrlArr[language].link + '?v=' + now.toString()
		window.open(pdfUrl, '_blank')
	}

	return (
		<View css={style}>
			<View className="shopDetailOuterView">
				<View className="shopDetailInnerView">
					<ShopOutline
						title="Kedem 4"
						subtitle="High-Performance MIDI Controller"
						images={images}
						qty={qty}
						onQtyChange={newQty => setQty(newQty)}
						product={thisProduct}
					/>

					<View className="shopDetailContentViwe">
						<View className="articleView">
							<Text category="h1">Kedem 4 User Manual</Text>

							<View className="userManualView">
								{Object.keys(kdm4Manual).map(aLang => {
									const title = kdm4Manual[aLang].title
									return (
										<Button
											key={`KDM4-${aLang}`}
											className="userManualOpenBtn"
											variant="contained"
											size="small"
											color="inherit"
											onClick={() => onManualOpenClick('KDM4', aLang)}
										>
											{`${title}`}
										</Button>
									)
								})}
							</View>
						</View>

						<View className="articleView">
							<Text category="h1">Kedem4: Redefining MIDI Controller</Text>
							<p className="contentText">
								Take your performance to the next level with <b>Kedem4</b>, the ultimate
								MIDI controller built for precision, flexibility, and ease of use. Whether
								you’re on stage or in the studio, Kedem4 gives you unparalleled control
								over your sound, empowering you to focus on your creativity.
							</p>
						</View>

						<View className="articleView">
							<Text category="h1">Specifications</Text>

							<View className="subarticleView">
								<ul>
									<li>
										<b>Dimensions: </b>
										140mm (W) x 121mm (D) x 62mm (H)
									</li>
									<li>
										<b>Weight: </b>
										760g (1.68 lbs)
									</li>
									<li>
										<b>Power: </b>
										9V DC (Center Negative) Current Draw: 250mA
									</li>
									<li>
										<b>Display: </b>
										3.5&quot; touch LCD
									</li>
									<li>
										<b>MIDI Ports: </b>
										1x MIDI IN, 1x MIDI OUT, 1x USB MIDI OUT, 1x TRS MIDI, 1x TRS
										MIDI/Relay
									</li>
									<li>
										<b>External Controls: </b>
										1x External Switch Inputs, 1x Expression Pedal Input
									</li>
									<li>
										<b>Wireless MIDI: </b>
										AirMIDI Technology (Exclusive to Judahs Products)
									</li>
								</ul>
							</View>
							<View className="contentView">
								<Image src={content1} className="contentImage1" />
							</View>
						</View>

						<Divider />

						<View className="articleView">
							<Text category="h1">Why Choose Kedem4?</Text>

							<View className="subarticleView">
								<Text category="h4">1. Effortless Control with LCD Touch Display</Text>
								<ul>
									<li>
										Easily manage all settings directly on the bright LCD touchscreen.
									</li>
									<li>
										No need for additional apps or computers—intuitive control at your
										fingertips.
									</li>
									<li className="li">
										Perfect for live performances, allowing quick adjustments without
										needing to connect a computer or app.
									</li>
								</ul>

								<View className="subarticleContentView">
									<Image src={content2} className="contentImage" />
								</View>
							</View>

							<View className="subarticleView">
								<Text category="h4">2. Versatile Action Switches</Text>
								<ul>
									<li className="li">
										Each switch offers up to 8 actions: One Press, Double Press, Long
										Press, Double Long Press, and even Release actions for each.
									</li>
									<li className="li">
										Highly intuitive LED indicators make real-time navigation easy.
									</li>
									<li className="li">
										Fully customizable switches let you assign functions like Tap Tempo or
										Bank Up/Down to any switch.
									</li>
									<li className="li">
										Combine two switches for even more functionality.
									</li>
								</ul>
							</View>

							<View className="subarticleView">
								<Text category="h4">3. Comprehensive Connectivity Options</Text>
								<ul>
									<li>Equipped with all the ports you need:</li>
									<ul>
										<li>1x MIDI IN</li>
										<li>1x MIDI OUT</li>
										<li>1x USB MIDI OUT</li>
										<li>1x TRS MIDI</li>
										<li>1x TRS MIDI/Relay</li>
										<li>1x external switch inputs</li>
										<li>1x expression pedal input</li>
									</ul>
									<li>Designed to handle the most complex setups effortlessly.</li>
								</ul>

								<View className="subarticleContentView">
									<Image src={content3} className="contentImage" />
								</View>
							</View>

							<View className="subarticleView">
								<Text category="h4">4. Wireless MIDI Transmission</Text>
								<ul>
									<li>
										Features Judahs’ innovative <b>AirMIDI</b> technology for seamless
										wireless MIDI control.
									</li>
									<li>
										Compatible with future Judahs products for a completely cable-free
										experience.
									</li>
								</ul>
							</View>

							<View className="subarticleView">
								<Text category="h4">5. Generous Storage Space</Text>
								<ul>
									<li>
										Access up to <b>100 banks</b>, with <b>4 switches per bank</b> and{' '}
										<b>6 switch combinations</b> including <b>2 external switches</b>.
										Each capable of handling <b>8 actions</b> based on switch
										configurations as presets.
									</li>
									<li>
										Each preset can store up to <b>30 MIDI data messages</b>.
									</li>
									<li>
										In total, one bank can accommodate up to <b>12 switches</b>,{' '}
										<b>96 actions</b>, and <b>2880 MIDI messages</b>.
									</li>
								</ul>
							</View>

							<View className="subarticleView">
								<Text category="h4">6. Intuitive Interface for Maximum Efficiency</Text>
								<ul>
									<li>Easily copy and paste MIDI presets for quick configuration.</li>
									<li>
										Save pedal information and switch channels effortlessly when needed.
									</li>
								</ul>
							</View>

							<View className="subarticleView">
								<Text category="h4">7. Innovative Expression Pedal Control</Text>
								<ul>
									<li>
										Achieve precise control with the ability to set the zero point of your
										expression pedal for greater accuracy.
									</li>
									<li>
										Control up to <b>5 MIDI CC messages</b> per switch, giving you
										enhanced control over your sound.
									</li>
								</ul>
							</View>

							<View className="subarticleView">
								<Text category="h4">8. Dynamic MIDI CC for Smooth Transitions</Text>
								<ul>
									<li>
										Kedem series introduces <b>Dynamic CC</b>, allowing MIDI CC values to
										change gradually over a time range of <b>0.1 to 10 seconds</b>.
									</li>
									<li>
										Ideal for creating smooth and natural transitions in sound, such as
										progressive volume swells or filter sweeps.
									</li>
								</ul>

								<View className="subarticleContentView">
									<Image src={dinamicCc} className="contentImage" />
								</View>
							</View>
						</View>

						<Divider />

						<View className="articleView">
							<Text category="h1">Elevate Your Sound with Kedem4</Text>
							<Text className="contentText" category="p1">
								With its cutting-edge design, user-friendly interface, and unparalleled
								versatility, Kedem4 is the ultimate tool for guitarists who demand
								precision and flexibility. Whether you&apos;re performing live, recording
								in the studio, or rehearsing, Kedem4 ensures you stay in complete control
								of your sound. Upgrade your setup today and experience the difference with
								Kedem4!
							</Text>

							<View className="subarticleContentView">
								<Image src={contentEnd} className="contentImage" />
							</View>
						</View>
					</View>
				</View>
			</View>
			<Footer />
		</View>
	)
}

export default ShopKedem4Page

const style = css({
	width: '100%',

	'.shopDetailOuterView': {
		borderBottom: '1px solid',
		borderColor: grey[900],

		'.shopDetailInnerView': {
			width: '80rem',
			margin: 'auto',

			'.shopDetailContentViwe': {
				width: 'calc(100% - 30rem)',
				textAlign: 'left',

				'.userManualView': {
					'.userManualOpenBtn': {
						marginRight: '0.5rem',
					},
				},

				'.articleView': {
					marginTop: '6rem',
					marginBottom: '6rem',

					'.contentText': {
						fontSize: '22px',
						lineHeight: 1.5,
					},

					'.subarticleView': {
						marginBottom: '4rem',

						li: {
							fontSize: '22px',
							lineHeight: 1.5,
						},
					},

					'.subarticleContentView': {
						width: '100%',
						textAlign: 'center',
						'.contentImage': {
							width: '50%',
						},
					},
				},

				'.contentView': {
					width: '100%',
					textAlign: 'center',

					'.contentImage1': {
						width: '60%',
					},
				},
			},
		},
	},

	'@media (max-width: 1350px)': {
		'.shopDetailOuterView': {
			padding: '0 2rem',
			'.shopDetailInnerView': {
				width: '100%',
			},
		},
	},

	'@media (max-width: 900px)': {
		'.shopDetailOuterView': {
			'.shopDetailInnerView': {
				'.shopDetailContentViwe': {
					width: '100%',

					'.articleView': {
						'.subarticleContentView': {
							'.contentImage': {
								width: '80%',
							},
						},
					},

					'.contentView': {
						width: '100%',
						textAlign: 'center',

						'.contentImage1': {
							width: '80%',
						},
					},
				},
			},
		},
	},
})
